import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import { payAPI } from '../../../../../services/redwolfSdkAPI';
import { setClientInstance } from '../../../../../store/paySlice';
import Modal from '../../../../common/components/Modal';

import Spinner from '../../../../common/components/Spinner';
import braintree from 'braintree-web';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import { openNotification } from '../../../../../store/commonSlice';
import { notification } from '../../../../../utils/constants';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';
import { useRecaptcha } from '../../../../common/hooks/useRecaptcha';

const errorState = {
	isCardNumberError: false,
	isExpirationError: false,
	isCVVError: false,
	isPostalCodeError: false,
	isCardHolderNameError: false
};

let hostedFieldsInstance: any = null;

interface AddCardProps {
	getCreditCards: () => void;
	isAddCardModalOpen: boolean;
	closeAddCardModal: () => void;
}

const AddCard = ({ getCreditCards, isAddCardModalOpen, closeAddCardModal }: AddCardProps) => {
	const cardErrorMessages = {
		numberMessage: 'Invalid card number. Use accepted card provider.',
		expirationDateMessage: 'Invalid expiration date.',
		cvvMessage: 'CVV is incorrect',
		postalCode: 'ZIP Code does not match',
		cardHolderName: 'Please input valid name.'
	};
	const [creditCardErrorMessages, setCreditCardErrorMessages] = useState('');
	const [cardErrorState, setCardErrorState] = useState(errorState);
	const [commorErrorMessage, setCommonErrorMessage] = useState('');
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(true);
	const [isFormInvalid, setIsFormInvalid] = useState(true);
	const [isSetDefaultCard, setISSetDefaultCard] = useState(false);
	const [isRecaptchaLoading, generateRecaptchaToken]: any = useRecaptcha();

	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();

	const dispatch = useDispatch();
	const token = getToken();

	const { clientInstance } = useSelector((state: any) => state.pay);

	const transaction = Sentry.startInactiveSpan({ name: 'Add Card' });

	useEffect(() => {
		setIsSpinnerLoading(!isRecaptchaLoading);
	}, [isRecaptchaLoading]);

	useEffect(() => {
		if (!clientInstance) {
			requestRedwolfService(payAPI.getBraintreeClientToken())
				.then((response: any) => {
					if (response.constructor.$metadata$.simpleName === 'Success') {
						createClientInstance(response.data.token);
					} else {
						handleTechnicalError();
					}
					transaction && transaction.end();
				})
				.catch((clientTokenError: any) => {
					Sentry.captureException('getBraintreeClientToken:' + clientTokenError);

					transaction && transaction.end();
					handleTechnicalError();
				});
		} else {
			createHostedFields(clientInstance);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const createClientInstance = (braintreeClientToken: any) => {
		braintree.client.create(
			{
				authorization: braintreeClientToken
			},
			(clientError: any, clientInstance: any) => {
				if (clientError) {
					handleTechnicalError();
				} else {
					dispatch(setClientInstance(clientInstance));
					createHostedFields(clientInstance);
				}
			}
		);
	};

	const createHostedFields = (clientInstance: any) => {
		const brainTreeModule: any = braintree;
		brainTreeModule.hostedFields.create(
			{
				client: clientInstance,
				styles: {
					input: {
						'font-size': '18px',
						'line-height': '24px',
						margin: '0',
						color: 'black'
					},
					':focus': {
						border: '2px solid #aadede',
						'box-shadow': 'none'
					},
					'.invalid': {
						color: 'black'
					}
				},
				fields: {
					number: {
						container: '#card-number',
						supportedCardBrands: {
							visa: true,
							mastercard: true,
							'american-express': true
						}
					},
					cardholderName: {
						container: '#cc-name'
					},
					cvv: {
						container: '#cvv'
					},
					expirationDate: {
						container: '#expiration-date',
						internalLabel: 'Expiration Date MM/YY'
					},
					postalCode: {
						container: '#postal-code',
						minlength: 5,
						maxlength: 5
					}
				}
			},
			(instanceError: any, instance: any) => {
				if (instanceError) {
					handleTechnicalError();
				} else {
					hostedFieldsInstance = instance;
					setIsSpinnerLoading(false);

					instance.on('blur', (event: any) => {
						const field = event.fields[event.emittedBy];

						if (!field.isEmpty) {
							field.container.classList.add('active');
						} else {
							field.container.classList.remove('active');
						}

						if (event.emittedBy === 'cardholderName') {
							if (!field.isEmpty && !field.isValid) {
								field.container.parentNode.classList.add('invalid');
								field.container.nextSibling.nextSibling.classList.add('invalid');
							} else {
								field.container.parentNode.classList.remove('invalid');
								field.container.nextSibling.nextSibling.classList.remove('invalid');
							}
						}

						setCardErrorMessage(event);

						const state = instance.getState();
						let formIsInvalid = false;
						Object.keys(state.fields).forEach((field) => {
							if (!state.fields[field].isValid) {
								formIsInvalid = true;
							}
						});

						const errorFields = document.querySelectorAll('.invalid');

						if (errorFields && errorFields.length === 0 && !formIsInvalid) {
							setIsFormInvalid(false);
							setCommonErrorMessage('');
						}
					});

					instance.on('empty', function (event: any) {
						const field = event.fields[event.emittedBy];
						field.container.parentNode.classList.remove('invalid');

						if (event.emittedBy === 'number') {
							errorState.isCardNumberError = false;
						} else if (event.emittedBy === 'cardholderName') {
							errorState.isCardHolderNameError = false;
						} else if (event.emittedBy === 'expirationDate') {
							errorState.isExpirationError = false;
						} else if (event.emittedBy === 'cvv') {
							errorState.isCVVError = false;
						} else if (event.emittedBy === 'postalCode') {
							errorState.isPostalCodeError = false;
						}

						setFieldInvalidAttribute(event.emittedBy, false);

						setCardErrorState({ ...errorState });
					});

					instance.on('validityChange', (event: any) => {
						setCardErrorMessage(event);

						const state = instance.getState();
						let formIsInvalid = false;
						Object.keys(state.fields).forEach((field) => {
							if (!state.fields[field].isValid) {
								formIsInvalid = true;
							}
						});

						const errorFields = document.querySelectorAll('.invalid');

						if (formIsInvalid) {
							setIsFormInvalid(true);
						} else if (errorFields && errorFields.length === 0) {
							setIsFormInvalid(false);
							setCommonErrorMessage('');
							setCreditCardErrorMessages('');
						} else {
							setIsFormInvalid(true);
						}
					});

					instance.setAttribute({
						field: 'number',
						attribute: 'aria-required',
						value: true
					});

					instance.setAttribute({
						field: 'cardholderName',
						attribute: 'aria-required',
						value: true
					});

					instance.setAttribute({
						field: 'cvv',
						attribute: 'aria-required',
						value: true
					});

					instance.setAttribute({
						field: 'expirationDate',
						attribute: 'aria-required',
						value: true
					});

					instance.setAttribute({
						field: 'postalCode',
						attribute: 'aria-required',
						value: true
					});
				}
			}
		);
	};

	const setCardErrorMessage = (event: any) => {
		const field = event.fields[event.emittedBy];

		if (!field.isEmpty && !field.isValid) {
			field.container.parentNode.classList.add('invalid');
			field.container.nextSibling.nextSibling.classList.add('invalid');
		} else {
			field.container.parentNode.classList.remove('invalid');
			field.container.nextSibling.nextSibling.classList.remove('invalid');
		}

		if (event.emittedBy === 'number') {
			if (!field.isEmpty && !field.isValid) {
				errorState.isCardNumberError = true;
				setCreditCardErrorMessages('');
			} else {
				errorState.isCardNumberError = false;
			}
			setFieldInvalidAttribute(event.emittedBy, errorState.isCardNumberError);
		} else if (event.emittedBy === 'cardholderName') {
			if (!field.isEmpty && !field.isValid) {
				errorState.isCardHolderNameError = true;
			} else {
				errorState.isCardHolderNameError = false;
			}
			setFieldInvalidAttribute(event.emittedBy, errorState.isCardHolderNameError);
		} else if (event.emittedBy === 'expirationDate') {
			if (!field.isEmpty && !field.isValid) {
				errorState.isExpirationError = true;
			} else {
				errorState.isExpirationError = false;
			}
			setFieldInvalidAttribute(event.emittedBy, errorState.isExpirationError);
		} else if (event.emittedBy === 'cvv') {
			if (!field.isEmpty && !field.isValid) {
				errorState.isCVVError = true;
			} else {
				errorState.isCVVError = false;
			}
			setFieldInvalidAttribute(event.emittedBy, errorState.isCVVError);
		} else if (event.emittedBy === 'postalCode') {
			if (!field.isEmpty && !field.isValid) {
				errorState.isPostalCodeError = true;
			} else {
				errorState.isPostalCodeError = false;
			}
			setFieldInvalidAttribute(event.emittedBy, errorState.isPostalCodeError);
		}

		setCardErrorState({ ...errorState });
	};

	const setFieldInvalidAttribute = (field: string, invalidStatus: boolean) => {
		hostedFieldsInstance.setAttribute({
			field: field,
			attribute: 'aria-invalid',
			value: invalidStatus
		});
	};

	const addCreditCardsSubmit = async (event: any) => {
		event.preventDefault();
		setIsSpinnerLoading(true);

		setCommonErrorMessage('');

		const captchaToken = await generateRecaptchaToken('vz_account_add_credit_card').then(
			(response: any) => response
		);

		if (hostedFieldsInstance) {
			hostedFieldsInstance.tokenize((tokenizeError: any, data: any) => {
				if (data) {
					const newCreditCardInfo = {
						billingAddress: {
							firstName: data.details.cardholderName,
							postalCode: ''
						},
						cardNonce: data.nonce,
						isDefault: isSetDefaultCard,
						reCaptchaToken: captchaToken
					};
					requestRedwolfService(payAPI.addCreditCard(token, newCreditCardInfo))
						.then((response: any) => {
							setIsSpinnerLoading(false);

							if (response.constructor.$metadata$.simpleName === 'Success') {
								addCardModalClose();

								logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CC_ADDED');
								dispatch(
									openNotification({
										type: 'SUCCESS',
										heading: 'Success',
										body: notification.message.addCreditCardSuccess
									})
								);

								getCreditCards();
							} else {
								setIsFormInvalid(true);

								logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_ADD_CC_ERROR', { error: response });
								if (response.response && response.response.status !== 400) {
									Sentry.captureException(new Error('addCreditCard:' + response));
								}

								const state = hostedFieldsInstance.getState();

								if (
									response.response.detail === 'Gateway Rejected: avs' ||
									response.response.detail === 'Address postal code is invalid' ||
									response.response.detail.toLowerCase().includes('zip code')
								) {
									state.fields['postalCode'].container.parentNode.classList.add('invalid');
									state.fields['postalCode'].container.nextSibling.nextSibling.classList.add(
										'invalid'
									);
									errorState.isPostalCodeError = true;
									setFieldInvalidAttribute('postalCode', true);
									hostedFieldsInstance.focus('postalCode');
								} else if (response.response.detail === 'Gateway Rejected: avs_and_cvv') {
									Object.keys(state.fields).forEach((field: any) => {
										if (
											field === 'number' ||
											field === 'expirationDate' ||
											field === 'cvv' ||
											field === 'postalCode'
										) {
											state.fields[field].container.parentNode.classList.add('invalid');
											state.fields[field].container.nextSibling.nextSibling.classList.add(
												'invalid'
											);
											setFieldInvalidAttribute(field, true);
										}
									});
									setCommonErrorMessage('Invalid card details. Try again.');
									hostedFieldsInstance.focus('number');
								} else if (response.response.detail === 'Gateway Rejected: cvv') {
									Object.keys(state.fields).forEach((field: any) => {
										if (field === 'number' || field === 'expirationDate' || field === 'cvv') {
											state.fields[field].container.parentNode.classList.add('invalid');
											state.fields[field].container.nextSibling.nextSibling.classList.add(
												'invalid'
											);
											setFieldInvalidAttribute(field, true);
										}
									});
									setCommonErrorMessage('Invalid card details. Try again.');
									hostedFieldsInstance.focus('number');
								} else {
									if (response.response.detail) {
										state.fields['number'].container.parentNode.classList.add('invalid');
										state.fields['number'].container.nextSibling.nextSibling.classList.add(
											'invalid'
										);
										errorState.isCardNumberError = true;
										setFieldInvalidAttribute('number', true);
										setCreditCardErrorMessages(response.response.detail);
									} else {
										Object.keys(state.fields).forEach((field: any) => {
											if (field === 'number' || field === 'expirationDate' || field === 'cvv') {
												state.fields[field].container.parentNode.classList.add('invalid');
												state.fields[field].container.nextSibling.nextSibling.classList.add(
													'invalid'
												);
												setFieldInvalidAttribute(field, true);
											}
										});
										setCommonErrorMessage('Invalid card details. Try again.');
									}

									hostedFieldsInstance.focus('number');
								}

								setCardErrorState({ ...errorState });
							}
							transaction && transaction.end();
						})
						.catch((err: any) => {
							Sentry.captureException(err);
							handleTechnicalError();

							transaction && transaction.end();
						});
				} else {
					transaction && transaction.end();

					setIsSpinnerLoading(false);
					handleCardErrors(tokenizeError);
				}
			});
		}
	};

	const handleCardErrors = (tokenizeError: any) => {
		try {
			if (tokenizeError.type === 'NETWORK') {
				handleTechnicalError();
				return;
			}

			let isErrorInBillingAddress = false;
			if (
				tokenizeError.details &&
				tokenizeError.details.originalError &&
				tokenizeError.details.originalError.details &&
				tokenizeError.details.originalError.details.originalError
			) {
				const fieldErrors: any[] =
					tokenizeError.details.originalError.details.originalError.fieldErrors;
				if (fieldErrors) {
					fieldErrors.forEach((fieldLevel1: any) => {
						if (fieldLevel1.fieldErrors && fieldLevel1.fieldErrors.length) {
							fieldLevel1.fieldErrors.forEach((fieldLevel2: any) => {
								if (fieldLevel2.field === 'billingAddress') {
									isErrorInBillingAddress = true;
								}
							});
						}
					});
				}
			}

			const state = hostedFieldsInstance.getState();

			if (isErrorInBillingAddress) {
				Object.keys(state.fields).forEach((field: any) => {
					if (field === 'postalCode') {
						state.fields[field].container.parentNode.classList.add('invalid');
						state.fields[field].container.nextSibling.nextSibling.classList.add('invalid');
						setFieldInvalidAttribute(field, true);
					}
					if (field === 'number' || field === 'expirationDate' || field === 'cvv') {
						state.fields[field].container.parentNode.classList.remove('invalid');
						state.fields[field].container.nextSibling.nextSibling.classList.remove('invalid');
					}
				});
				hostedFieldsInstance.focus('postalCode');
			} else {
				Object.keys(state.fields).forEach((field: any) => {
					if (field === 'number' || field === 'expirationDate' || field === 'cvv') {
						state.fields[field].container.parentNode.classList.add('invalid');
						state.fields[field].container.nextSibling.nextSibling.classList.add('invalid');
						setFieldInvalidAttribute(field, true);
					}
					if (field === 'postalCode') {
						state.fields[field].container.parentNode.classList.remove('invalid');
						state.fields[field].container.nextSibling.nextSibling.classList.remove('invalid');
					}
				});
				setCommonErrorMessage('Invalid card details. Try again.');
				hostedFieldsInstance.focus('number');
			}
		} catch (err: any) {
			handleTechnicalError();
			Sentry.captureException(err);
		}
	};

	const handleTechnicalError = () => {
		setIsSpinnerLoading(false);
		showTechnicalErrorMessage();
	};

	const addCardModalClose = () => {
		if (hostedFieldsInstance !== null) {
			hostedFieldsInstance.clear('number');
			hostedFieldsInstance.clear('cardholderName');
			hostedFieldsInstance.clear('cvv');
			hostedFieldsInstance.clear('expirationDate');
			hostedFieldsInstance.clear('postalCode');
		}
		setCommonErrorMessage('');
		errorState.isCardNumberError = false;
		errorState.isCardHolderNameError = false;
		errorState.isCVVError = false;
		errorState.isExpirationError = false;
		errorState.isPostalCodeError = false;
		setCardErrorState({ ...errorState });
		closeAddCardModal();
		setISSetDefaultCard(false);

		const element = document.querySelectorAll('#braintree-component .hosted-field');

		[].forEach.call(element, function (el: any) {
			el.classList.remove('active');
		});
	};

	return (
		<>
			<Modal
				containerId="add-card"
				show={isAddCardModalOpen}
				onHide={addCardModalClose}
				heading="Add New Card"
				containerClasses="add-newcard-modal"
				overlaycontainerClass="cc-popup">
				<form className="edit-modal-form field">
					<div className="pay-icons payment-card-icons">
						<span className="txt">
							VIZIO Account accepts:
							<span className="cc-icons">
								<span className="cc-visa">
									<img src="images/visa-logo.png" alt="visa card" />
								</span>
								<span className="cc-master">
									<img src="images/mastercard-logo.svg" alt="Master card" />
								</span>
								<span className="cc-ae">
									<img src="images/american-express-logo.png" alt="American Express card" />
								</span>
							</span>
						</span>
					</div>

					<div id="braintree-component" className="cc-details">
						<div className="field">
							<div className="input-box form-group input-label">
								<div className="textfield--float-label card-number">
									<div id="card-number" className="hosted-field"></div>
									<label className="hosted-field--label" htmlFor="card-number">
										Credit Card Number
									</label>

									<div
										className="invalid-feedback-item"
										aria-live="polite"
										style={{ display: 'block' }}>
										{cardErrorState.isCardNumberError && (
											<span>
												{creditCardErrorMessages === ''
													? cardErrorMessages.numberMessage
													: creditCardErrorMessages}
											</span>
										)}
									</div>
								</div>
							</div>
						</div>

						<div className="field">
							<div className="input-box form-group input-label">
								<div className="textfield--float-label">
									<div id="cc-name" className="hosted-field"></div>
									<label className="hosted-field--label" htmlFor="cc-name">
										Name on Card
									</label>

									<div
										className="invalid-feedback-item"
										aria-live="polite"
										style={{ display: 'block' }}>
										{cardErrorState.isCardHolderNameError && (
											<span aria-live="assertive">{cardErrorMessages.cardHolderName}</span>
										)}
									</div>
								</div>
							</div>
						</div>

						<div className="cc-fields">
							<div className="exp-cvv">
								<div className="input-box form-group input-label cc-exp">
									<div className="textfield--float-label">
										<div id="expiration-date" className="hosted-field"></div>
										<label className="hosted-field--label" htmlFor="expiration-date">
											Exp. (MM/YY)
										</label>

										{cardErrorState.isExpirationError ? (
											<div className="invalid-feedback-item" style={{ display: 'block' }}>
												{cardErrorState.isExpirationError && (
													<span aria-live="assertive">
														{cardErrorMessages.expirationDateMessage}
													</span>
												)}
											</div>
										) : (
											<div
												className="invalid-feedback-item"
												aria-live="assertive"
												style={{ display: 'block' }}>
												<span>{commorErrorMessage}</span>
											</div>
										)}
									</div>
								</div>

								<div className="input-box form-group input-label cc-cvc">
									<div className="show-form card-question">
										<div className="textfield--float-label">
											<div id="cvv" className="hosted-field"></div>
											<label className="hosted-field--label" htmlFor="cvv">
												CVV
											</label>

											<div
												className="invalid-feedback-item"
												aria-live="polite"
												style={{ display: 'block' }}>
												{cardErrorState.isCVVError && <span>{cardErrorMessages.cvvMessage}</span>}
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="input-box form-group input-label cc-zip">
								<div className="show-form card-question">
									<div className="textfield--float-label">
										<div id="postal-code" className="hosted-field"></div>
										<label className="hosted-field--label" htmlFor="postal-code">
											ZIP Code
										</label>

										<div
											className="invalid-feedback-item"
											aria-live="polite"
											style={{ display: 'block' }}>
											{cardErrorState.isPostalCodeError && (
												<span>{cardErrorMessages.postalCode}</span>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="braintree-info">
						<p>Payment will be processed by external vendor, Braintree - A PayPal Service</p>
					</div>

					<div className="button-field-group">
						<div className="form-checkbox CheckBox default-checkbox">
							<input
								type="checkbox"
								id="set-add-default"
								autoComplete="off"
								checked={isSetDefaultCard}
								onChange={(event) => setISSetDefaultCard(event.target.checked)}
							/>
							<label htmlFor="set-add-default">Set as Default</label>
						</div>
						<button
							type="submit"
							className="primary-button"
							disabled={isFormInvalid}
							onClick={addCreditCardsSubmit}>
							Save
						</button>
					</div>
				</form>
			</Modal>

			{isSpinnerLoading && <Spinner />}
		</>
	);
};

export default AddCard;
